import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useReducedMotion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '@/shared/i18n';
import { BREAKPOINTS, NAVIGATION_EVENTS } from '@/shared/utils/constants';

const SCROLL_VISIBILITY_OFFSET = 96;
const ROUTE_SETTLE_DELAY = 380;
const DESKTOP_HOME_QUERY = `(min-width: ${BREAKPOINTS.MD}px)`;

type ScrollMetrics = {
  progress: number;
  scrollTop: number;
  maxScroll: number;
};

type HomeSectionState = {
  sectionIndex: number;
  sectionCount: number;
};

const clamp = (value: number) => Math.min(Math.max(value, 0), 1);
const easeOutCubic = (value: number) => 1 - Math.pow(1 - value, 3);

const getScrollableRouteContainer = () => {
  const explicitContainer = document.querySelector<HTMLElement>(
    '[data-route-scroll-container]'
  );

  if (explicitContainer) {
    return explicitContainer;
  }

  return Array.from(document.querySelectorAll<HTMLElement>('main')).find(
    element => {
      const { overflowY } = window.getComputedStyle(element);
      const canScroll = element.scrollHeight - element.clientHeight > 1;

      return canScroll && ['auto', 'scroll', 'overlay'].includes(overflowY);
    }
  );
};

const getWindowMetrics = (): ScrollMetrics => {
  const documentElement = document.documentElement;
  const body = document.body;
  const scrollHeight = Math.max(
    documentElement.scrollHeight,
    body?.scrollHeight ?? 0
  );
  const scrollTop =
    window.scrollY || documentElement.scrollTop || body?.scrollTop || 0;
  const maxScroll = Math.max(scrollHeight - window.innerHeight, 0);

  return {
    progress: maxScroll > 0 ? clamp(scrollTop / maxScroll) : 0,
    scrollTop,
    maxScroll
  };
};

const getElementMetrics = (element: HTMLElement): ScrollMetrics => {
  const maxScroll = Math.max(element.scrollHeight - element.clientHeight, 0);
  const scrollTop = element.scrollTop;

  return {
    progress: maxScroll > 0 ? clamp(scrollTop / maxScroll) : 0,
    scrollTop,
    maxScroll
  };
};

export const BackToTopButton = () => {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();
  const [metrics, setMetrics] = useState<ScrollMetrics>({
    progress: 0,
    scrollTop: 0,
    maxScroll: 0
  });
  const [isRouteSettling, setIsRouteSettling] = useState(false);
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const homeSectionRef = useRef<HomeSectionState>({
    sectionIndex: 0,
    sectionCount: 1
  });
  const updateFrameRef = useRef<number | null>(null);
  const progressFrameRef = useRef<number | null>(null);
  const displayedProgressRef = useRef(0);
  const routeSettlingRef = useRef(false);

  const isDesktopHome = useCallback(() => {
    return pathname === '/' && window.matchMedia(DESKTOP_HOME_QUERY).matches;
  }, [pathname]);

  const updateMetrics = useCallback(() => {
    if (routeSettlingRef.current) return;

    if (isDesktopHome() && homeSectionRef.current.sectionCount > 1) {
      const { sectionIndex, sectionCount } = homeSectionRef.current;
      const maxScroll = sectionCount - 1;

      setMetrics({
        progress: clamp(sectionIndex / maxScroll),
        scrollTop: sectionIndex,
        maxScroll
      });
      return;
    }

    const routeScrollContainer = getScrollableRouteContainer();

    setMetrics(
      routeScrollContainer
        ? getElementMetrics(routeScrollContainer)
        : getWindowMetrics()
    );
  }, [isDesktopHome]);

  useLayoutEffect(() => {
    routeSettlingRef.current = true;
    homeSectionRef.current = {
      sectionIndex: 0,
      sectionCount: 1
    };
    setIsRouteSettling(true);
    setMetrics({
      progress: 0,
      scrollTop: 0,
      maxScroll: 0
    });
    displayedProgressRef.current = 0;
    setDisplayedProgress(0);

    if (progressFrameRef.current !== null) {
      window.cancelAnimationFrame(progressFrameRef.current);
      progressFrameRef.current = null;
    }
  }, [pathname]);

  useEffect(() => {
    const delayedUpdate = window.setTimeout(() => {
      routeSettlingRef.current = false;
      updateMetrics();
      setIsRouteSettling(false);
    }, ROUTE_SETTLE_DELAY);

    return () => {
      window.clearTimeout(delayedUpdate);
    };
  }, [pathname, updateMetrics]);

  const scheduleMetricsUpdate = useCallback(() => {
    if (updateFrameRef.current !== null) return;

    updateFrameRef.current = window.requestAnimationFrame(() => {
      updateFrameRef.current = null;
      updateMetrics();
    });
  }, [updateMetrics]);

  useEffect(() => {
    updateMetrics();

    const delayedUpdate = window.setTimeout(updateMetrics, 120);

    document.addEventListener('scroll', scheduleMetricsUpdate, {
      capture: true,
      passive: true
    });
    window.addEventListener('scroll', scheduleMetricsUpdate, {
      passive: true
    });
    window.addEventListener('resize', scheduleMetricsUpdate);

    return () => {
      window.clearTimeout(delayedUpdate);
      document.removeEventListener('scroll', scheduleMetricsUpdate, {
        capture: true
      });
      window.removeEventListener('scroll', scheduleMetricsUpdate);
      window.removeEventListener('resize', scheduleMetricsUpdate);

      if (updateFrameRef.current !== null) {
        window.cancelAnimationFrame(updateFrameRef.current);
      }

      if (progressFrameRef.current !== null) {
        window.cancelAnimationFrame(progressFrameRef.current);
      }
    };
  }, [scheduleMetricsUpdate, updateMetrics]);

  useEffect(() => {
    const handleHomeSectionChange = (event: Event) => {
      const sectionEvent = event as CustomEvent<Partial<HomeSectionState>>;
      const nextSectionIndex = sectionEvent.detail?.sectionIndex;
      const nextSectionCount = sectionEvent.detail?.sectionCount;

      if (typeof nextSectionIndex !== 'number') return;

      homeSectionRef.current = {
        sectionIndex: nextSectionIndex,
        sectionCount:
          typeof nextSectionCount === 'number'
            ? Math.max(nextSectionCount, 1)
            : homeSectionRef.current.sectionCount
      };

      scheduleMetricsUpdate();
    };

    window.addEventListener(
      NAVIGATION_EVENTS.HOME_SECTION_CHANGE,
      handleHomeSectionChange
    );

    return () => {
      window.removeEventListener(
        NAVIGATION_EVENTS.HOME_SECTION_CHANGE,
        handleHomeSectionChange
      );
    };
  }, [scheduleMetricsUpdate]);

  useEffect(() => {
    const targetProgress = clamp(metrics.progress);

    if (progressFrameRef.current !== null) {
      window.cancelAnimationFrame(progressFrameRef.current);
      progressFrameRef.current = null;
    }

    if (prefersReducedMotion) {
      displayedProgressRef.current = targetProgress;
      setDisplayedProgress(targetProgress);
      return;
    }

    const startProgress = displayedProgressRef.current;
    const distance = targetProgress - startProgress;

    if (Math.abs(distance) < 0.001) {
      displayedProgressRef.current = targetProgress;
      setDisplayedProgress(targetProgress);
      return;
    }

    const duration = isDesktopHome() ? 460 : 200;
    const startedAt = performance.now();

    const updateProgress = (timestamp: number) => {
      const elapsed = timestamp - startedAt;
      const progress = clamp(elapsed / duration);
      const nextProgress = startProgress + distance * easeOutCubic(progress);

      displayedProgressRef.current = nextProgress;
      setDisplayedProgress(nextProgress);

      if (progress < 1) {
        progressFrameRef.current = window.requestAnimationFrame(updateProgress);
        return;
      }

      displayedProgressRef.current = targetProgress;
      setDisplayedProgress(targetProgress);
      progressFrameRef.current = null;
    };

    progressFrameRef.current = window.requestAnimationFrame(updateProgress);

    return () => {
      if (progressFrameRef.current !== null) {
        window.cancelAnimationFrame(progressFrameRef.current);
        progressFrameRef.current = null;
      }
    };
  }, [isDesktopHome, metrics.progress, prefersReducedMotion]);

  const handleBackToTop = () => {
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';

    if (isDesktopHome() && homeSectionRef.current.sectionCount > 1) {
      window.dispatchEvent(
        new CustomEvent(NAVIGATION_EVENTS.HOME_SECTION_CHANGE, {
          detail: {
            sectionIndex: 0,
            sectionCount: homeSectionRef.current.sectionCount
          }
        })
      );
      return;
    }

    const routeScrollContainer = getScrollableRouteContainer();

    if (routeScrollContainer) {
      routeScrollContainer.scrollTo({ top: 0, left: 0, behavior });
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior });
  };

  const isVisible =
    !isRouteSettling &&
    metrics.maxScroll > 0 &&
    (isDesktopHome()
      ? metrics.scrollTop > 0
      : metrics.scrollTop > SCROLL_VISIBILITY_OFFSET);
  const progressPercent = Math.round(clamp(metrics.progress) * 100);
  const progressDegrees = (clamp(displayedProgress) * 360).toFixed(2);
  const progressBackground = `conic-gradient(from -90deg, var(--color-accent-primary) 0deg ${progressDegrees}deg, var(--color-border-primary) ${progressDegrees}deg 360deg)`;

  return (
    <button
      type="button"
      aria-label={t.backToTop.ariaLabel(progressPercent)}
      aria-hidden={!isVisible}
      tabIndex={isVisible ? 0 : -1}
      className={`fixed right-4 bottom-4 z-[45] h-14 w-14 rounded-full p-[3px] shadow-2xl shadow-[#00bbc3]/20 outline-none transition-[opacity,transform,box-shadow] duration-200 ease-[cubic-bezier(0.76,0,0.24,1)] focus-visible:ring-2 focus-visible:ring-[#00bbc3] focus-visible:ring-offset-2 focus-visible:ring-offset-background-primary sm:right-5 sm:bottom-5 ${
        isVisible
          ? 'pointer-events-auto translate-y-0 scale-100 cursor-pointer opacity-100 sm:hover:scale-[1.04] active:scale-[0.96]'
          : 'pointer-events-none translate-y-3 scale-[0.92] opacity-0'
      }`}
      style={{
        background: progressBackground,
        transitionDuration: prefersReducedMotion ? '0ms' : undefined,
        willChange: prefersReducedMotion ? undefined : 'opacity, transform'
      }}
      onClick={handleBackToTop}
    >
      <span className="flex h-full w-full items-center justify-center rounded-full border border-white/60 bg-background-card/95 text-[#007d9b] backdrop-blur-xl transition-colors hover:bg-background-primary dark:border-white/10 dark:bg-[#0d1117]/95 dark:text-[#00bbc3]">
        <ArrowUp size={22} strokeWidth={2.5} aria-hidden="true" />
      </span>
    </button>
  );
};
